import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Chart, ChartData, TableDateRangeEnum } from "../../models/company.model";
import { useEffect, useMemo, useState } from "react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { getReportGroup, getReviews } from "../../actions/report.action";
import { useConfig } from "../../containers/ConfigContext";
import LoadingButton from "../LoadingButton";
import { getOptions, ReportDataModel, ReviewReport } from "../../models/report.model";
import { downloadDataIntoCSV, getUrlParam, titleStr } from "../../lib/helper";
import moment from "moment";
import _ from "lodash";
import { GroupSortTable } from "../../containers/GroupSortTable";
import { chartHeight, parseChartExpression, useGroupBy, varsInExpression } from "../../chart-helpers/group.table.helper";

export const reviewsRowsPerPage = 10;

export default function CustomizedTable(props: { chart: Chart; reports?: ReportDataModel }) {
  const { groupBy } = props.chart || {};
  return (
    <>
      {!groupBy && <ReviewsTable chart={props.chart} />}
      {groupBy && <GroupByTable chart={props.chart} reports={props.reports} key={JSON.stringify(props.chart)} />}
    </>
  );
}

function ReviewsTable(props: { chart: Chart }) {
  const { data: cols, label } = props.chart || {};
  const { loggedUser, filter, reviews, setReviews } = useConfig();
  const { offset, data: rows, total = 0 } = reviews || {};
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const onNextPage = async (currentPage: number) => {
    if (!rows?.[(currentPage + 1) * reviewsRowsPerPage] && offset) {
      setLoading(true);
      const { hierarchyFilter, ...rest } = filter || {};
      const param = {
        id: hierarchyFilter || getOptions(loggedUser)?.[0]?.value,
        offset,
        ...rest,
      };
      const res = await getReviews(param);
      //@ts-ignore
      setReviews({
        ...reviews,
        data: (rows || []).concat(res.reviews),
        offset: res.offset,
      });
      setLoading(false);
    }
    setPage(page + 1);
  };

  const onDownload = async () => {
    let all: ReviewReport[] = [];
    const { hierarchyFilter, ...rest } = filter || {};
    const param = {
      id: hierarchyFilter || getOptions(loggedUser)?.[0]?.value,
      offset: undefined,
      limit: 100,
      ...rest,
    };
    do {
      const res = await getReviews(param);
      all = all.concat(res.reviews);
      param.offset = res.offset;
    } while (param.offset);
    const csv: string[][] = [[...cols.map((d: any) => d.label)]];
    all.forEach((review: ReviewReport, index: number) => {
      csv[index + 1] = [
        ...cols.map((c) =>
          typeof review[c.expression] === "number"
            ? review[c.expression].toFixed(c.decimalPlaces || 0)
            : review[c.expression]
        ),
      ];
    });
    downloadDataIntoCSV(csv.map((row) => row.join(",")).join("\n"), `Reviews`);
  };

  const mobile = getUrlParam("mobile");

  return (
    <Stack spacing={2} key={page}>
      <Paper elevation={2} className="padding-small">
        <Typography variant={"h6"} textAlign={"center"}>
          {label || "\u00A0"}
        </Typography>
      </Paper>
      <Paper elevation={2} className="padding-normal">
        <div
          style={{
            height: chartHeight * 0.9,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {!loading && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  {cols.map((c) => (
                    <TableCell key={`${c.expression}${c.label}`}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.slice(page * reviewsRowsPerPage, (page + 1) * reviewsRowsPerPage).map((row, index) => {
                  return (
                    <TableRow key={row.surveyEventSubmitId}>
                      <TableCell>{index + 1 + page * reviewsRowsPerPage}</TableCell>
                      {cols.map((c) => (
                        <TableCell key={`${row.surveyEventSubmitId}${c.expression}${c.label}`}>
                          {typeof row[c.expression] === "number"
                            ? row[c.expression].toFixed(c.decimalPlaces || 0)
                            : row[c.expression]}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {loading && (
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={4} height={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </div>
        <Stack direction={"row"} justifyContent={"space-between"} style={{ height: chartHeight * 0.1 }}>
          <div style={{ marginTop: 2 }}>
            {!mobile && onDownload ? (
              <LoadingButton onClick={onDownload} variant="text">
                Download
              </LoadingButton>
            ) : (
              <Box style={{ width: 102 }}></Box>
            )}
          </div>
          <div>
            <IconButton onClick={() => setPage(page - 1)} disabled={page <= 0}>
              <ArrowBack />
            </IconButton>
          </div>
          <div>
            <IconButton onClick={() => onNextPage(page)} disabled={page + 1 >= Math.ceil(total / reviewsRowsPerPage)}>
              <ArrowForward />
            </IconButton>
          </div>
          <Box style={{ width: 102 }}></Box>
        </Stack>
      </Paper>
    </Stack>
  );
}

function GroupByTable(props: { chart: Chart; reports?: ReportDataModel }) {
  const { data: cols, groupBy, label, storeLabelTypes } = props.chart || {};
  const headCells = useMemo(() => {
    return [
      {
        id: "groupLabel",
        label: groupBy === "stores" ? "Locations" : titleStr(groupBy),
        align: "left",
      },
      ...cols.map((col: ChartData) => ({
        id: `${col.expression}${col.label}`,
        label: col.label,
        dataFormation: col.tableDataFormation,
        decimalPlaces: col.decimalPlaces,
      })),
    ];
  }, [cols, groupBy]);

  const { loading: groupLoading, groups } = useGroupBy(props);
  const rows = useMemo(() => {
    const items = _.zip(...groups).map((items) => {
      const res: Record<string, number | string> = {};
      cols.forEach((col, index) => {
        const item = items[index];
        res.id = item?.id || "";
        res.groupLabel = item?.groupLabel || "";
        res[`${col.expression}${col.label}`] = parseChartExpression(item, col.expression, col.decimalPlaces);
      });
      return res;
    });
    return items;
  }, [groups, cols]);

  return (
    <Stack spacing={2}>
      <Paper elevation={2} className="padding-small">
        <Typography variant={"h6"} textAlign={"center"}>
          {label || "\u00A0"}
        </Typography>
      </Paper>
      <Paper elevation={2} className="padding-r-normal padding-l-normal padding-t-normal">
        <div
          style={{
            height: chartHeight + 12,
            overflow: "hidden",
          }}
        >
          {!groupLoading && (
            <GroupSortTable headCells={headCells} rows={rows as any} groupBy={groupBy || ""} storeLabelTypes={storeLabelTypes} />
          )}
          {groupLoading && (
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={4} height={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </div>
      </Paper>
    </Stack>
  );
}

